import { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdSend, MdClose } from 'react-icons/md';

export default function Notifications() {
  const [allCities, setAllCities] = useState([]);
  const [target, setTarget] = useState('all'); // 'all' | 'state' | 'city'
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [image, setImage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const uploadImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/admin/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setImage(res.data.data.url);
        toast.success('Image uploaded');
      } catch {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  useEffect(() => {
    api.get('/cities').then(res => setAllCities(res.data.data || [])).catch(() => {});
  }, []);

  const states = useMemo(() => {
    const raw = allCities.map(c => c.state ? c.state.trim() : null).filter(Boolean);
    return [...new Set(raw)];
  }, [allCities]);

  const citiesForState = useMemo(() => {
    if (!selectedState) return [];
    return allCities.filter(c => c.state && c.state.trim() === selectedState);
  }, [allCities, selectedState]);

  const handleSend = async () => {
    if (sending) return;
    if (!title.trim() || !body.trim()) return toast.error('Title and body are required');
    if (target === 'state' && !selectedState) return toast.error('Pick a state');
    if (target === 'city' && !selectedCity) return toast.error('Pick a city');

    setSending(true);
    try {
      await api.post('/admin/notifications/broadcast', {
        target,
        state: target === 'state' ? selectedState : undefined,
        city: target === 'city' ? selectedCity : undefined,
        title: title.trim(),
        body: body.trim(),
        image: image || undefined,
      });
      toast.success('Notification queued successfully');
      setTitle('');
      setBody('');
      setImage('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>Notifications</h2>
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        <div className="section-header">
          <h3>Send Push Notification</h3>
        </div>

        <div className="form-group">
          <label>Send to</label>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', minHeight: 42, alignItems: 'center' }}>
            {[
              { value: 'all', label: 'All India' },
              { value: 'state', label: 'By State' },
              { value: 'city', label: 'By City' },
            ].map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="radio"
                  name="target"
                  checked={target === opt.value}
                  onChange={() => {
                    setTarget(opt.value);
                    setSelectedState('');
                    setSelectedCity('');
                  }}
                  style={{ cursor: 'pointer', width: 16, height: 16, accentColor: '#EF233C' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {(target === 'state' || target === 'city') && (
          <div className="form-group">
            <label>State</label>
            <select
              value={selectedState}
              onChange={e => { setSelectedState(e.target.value); setSelectedCity(''); }}
            >
              <option value="">Select a state…</option>
              {states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}

        {target === 'city' && (
          <div className="form-group">
            <label>City</label>
            <select
              value={selectedCity}
              onChange={e => setSelectedCity(e.target.value)}
              disabled={!selectedState}
            >
              <option value="">{selectedState ? 'Select a city…' : 'Pick a state first'}</option>
              {citiesForState.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Title</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="Notification title" />
        </div>

        <div className="form-group">
          <label>Body</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={4} placeholder="Notification message" />
        </div>

        <div className="form-group">
          <label>Image (optional)</label>
          {image ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={image} alt="attachment" style={{ maxWidth: 220, maxHeight: 140, borderRadius: 8, display: 'block', objectFit: 'cover' }} />
              <button
                type="button"
                onClick={() => setImage('')}
                style={{ position: 'absolute', top: -8, right: -8, background: '#DC2626', color: '#fff', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                title="Remove image"
              >
                <MdClose size={14} />
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn-sm btn-ghost" onClick={uploadImage} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Attach Image'}
            </button>
          )}
        </div>

        <button className="btn btn-primary" onClick={handleSend} disabled={sending} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <MdSend /> {sending ? 'Sending…' : 'Send Notification'}
        </button>
      </div>
    </div>
  );
}
